import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import IamEmergencyRoleRegistry from '@devnw/cyber-iam/iam/EmergencyRoleRegistry/EmergencyRoleRegistry'
import type {
  EmergencyRole,
  EmergencyRoleSubmission,
} from '@devnw/cyber-iam/iam/types'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'
import { toIamEmergencyRole, type WireEmergencyRole } from '../api/breakGlassMapping'

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Request failed'
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

const MutedText = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

/**
 * OIDCPAM Gate-6 Phase 5b: migrated off MUI onto
 * @devnw/cyber-iam/iam/EmergencyRoleRegistry — the exact parity component
 * built from this page. The component owns the registered-role list surface
 * (loading/error/empty rendered from props) and the create form (its own
 * validation, submitting state, and success/error notes); this page still
 * owns the API calls and the wire-mapping (../api/breakGlassMapping — fixing
 * a latent camelCase/snake_case bug, see that file's header).
 */
export const EmergencyRoles = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()
  const [roles, setRoles] = useState<EmergencyRole[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback((): void => {
    if (!tenant || tenant === 'system') {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    client
      .adminListEmergencyRoles({ tenantId: tenant })
      .then((r) => {
        setRoles(
          ((r.items ?? []) as unknown as WireEmergencyRole[]).map(
            toIamEmergencyRole
          )
        )
      })
      .catch((e: unknown) => setError(errMessage(e)))
      .finally(() => setLoading(false))
  }, [client, tenant])

  useEffect(load, [load])

  const handleCreate = async (
    submission: EmergencyRoleSubmission
  ): Promise<void> => {
    await client.adminCreateEmergencyRole({
      tenantId: tenant,
      name: submission.name,
      resourceId: submission.resourceId,
      maxTtlSeconds: submission.maxTtlSeconds,
      permissions: submission.permissions,
      scope: submission.scope,
    })
  }

  if (tenant === 'system') {
    return (
      <PageContainer>
        <MutedText>
          Please select a tenant to manage emergency roles.
        </MutedText>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <IamEmergencyRoleRegistry
        testID="emergency-roles"
        title="Emergency Roles"
        roles={roles}
        onCreate={handleCreate}
        onCreated={() => load()}
        loading={loading}
        error={error ?? undefined}
      />
    </PageContainer>
  )
}
